import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  AtSign,
  Calendar,
  Clock,
  IdCard,
  Languages,
  Mail,
  MapPin,
  Shield,
  Trees,
  UserCog,
} from 'lucide-react';
import type { AdminUser } from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { api } from '@/lib/api-client';

const ROLE_COLORS: Record<string, 'blue' | 'green' | 'yellow' | 'gray'> = {
  SUPER_ADMIN: 'blue',
  CONTROLOR: 'green',
  ADMINISTRATOR: 'yellow',
  AJUTOR_ADMINISTRATOR: 'gray',
};

export function UserDetailPage() {
  const { t, i18n } = useTranslation(['admin', 'common', 'auth']);
  const { id } = useParams<{ id: string }>();

  const userQ = useQuery<AdminUser>({
    queryKey: ['admin', 'users', id],
    queryFn: () => api.get(`admin/users/${id}`).json<AdminUser>(),
    enabled: !!id,
  });

  // Manager lookup if the row carries a managerId. Best-effort: we
  // surface the manager's name + username inline so the SUPER_ADMIN
  // doesn't have to chase the link.
  const managerQ = useQuery<AdminUser>({
    queryKey: ['admin', 'users', userQ.data?.managerId],
    queryFn: () =>
      api.get(`admin/users/${userQ.data!.managerId}`).json<AdminUser>(),
    enabled: !!userQ.data?.managerId,
  });

  const u = userQ.data;
  const fmt = (iso: string | null | undefined) =>
    iso
      ? new Date(iso).toLocaleString(i18n.language, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  return (
    <div className="animate-fade-in mx-auto max-w-3xl space-y-4 py-6">
      <Link
        to="/app/admin/users"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
      >
        <ArrowLeft className="h-4 w-4" />
        {t('admin:userDetail.back', { defaultValue: 'Înapoi la utilizatori' })}
      </Link>

      {userQ.isLoading || !u ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-forest-100 text-lg font-bold text-forest-700 dark:bg-forest-900/40 dark:text-forest-300">
              {(u.fullName || u.username || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
                {u.fullName}
              </h1>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                <span className="font-mono">@{u.username}</span>
                <Badge tone={ROLE_COLORS[u.role] ?? 'gray'}>
                  {u.role.replace(/_/g, ' ')}
                </Badge>
                {u.isActive ? (
                  <Badge tone="green">
                    {t('admin:userDetail.active', { defaultValue: 'Activ' })}
                  </Badge>
                ) : (
                  <Badge tone="gray">
                    {t('admin:userDetail.inactive', { defaultValue: 'Inactiv' })}
                  </Badge>
                )}
              </div>
            </div>
          </div>

          <Card>
            <CardHeader>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <UserCog className="h-4 w-4" />
                {t('admin:userDetail.identity', { defaultValue: 'Identitate' })}
              </h2>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <Row icon={AtSign} label={t('admin:userDetail.username', { defaultValue: 'Nume utilizator' })}>
                <code className="font-mono">{u.username}</code>
              </Row>
              <Row icon={IdCard} label={t('admin:userDetail.code', { defaultValue: 'Cod autor' })}>
                {u.cod ? <code className="font-mono">{u.cod}</code> : '—'}
              </Row>
              <Row icon={Mail} label={t('admin:userDetail.email', { defaultValue: 'Email' })}>
                {u.email ? (
                  <a
                    href={`mailto:${u.email}`}
                    className="text-forest-700 hover:underline dark:text-forest-300"
                  >
                    {u.email}
                  </a>
                ) : (
                  '—'
                )}
              </Row>
              <Row icon={Languages} label={t('admin:userDetail.language', { defaultValue: 'Limbă preferată' })}>
                {u.preferredLanguage.toUpperCase()}
              </Row>
              <Row icon={Shield} label={t('admin:userDetail.role', { defaultValue: 'Rol' })}>
                <Badge tone={ROLE_COLORS[u.role] ?? 'gray'}>
                  {u.role.replace(/_/g, ' ')}
                </Badge>
              </Row>
              {u.role === 'AJUTOR_ADMINISTRATOR' && u.managerId ? (
                <Row icon={UserCog} label={t('admin:userDetail.manager', { defaultValue: 'Manager' })}>
                  {managerQ.data ? (
                    <Link
                      to={`/app/admin/users/${u.managerId}`}
                      className="text-forest-700 hover:underline dark:text-forest-300"
                    >
                      {managerQ.data.fullName} (@{managerQ.data.username})
                    </Link>
                  ) : (
                    <code className="font-mono text-xs text-slate-500">
                      {u.managerId.slice(0, 8)}…
                    </code>
                  )}
                </Row>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <Trees className="h-4 w-4" />
                {t('admin:userDetail.entities', { defaultValue: 'Entități silvice' })}
              </h2>
            </CardHeader>
            <CardBody className="text-sm">
              {u.assignedEntitatiSilvice.length === 0 ? (
                <p className="italic text-slate-500 dark:text-slate-400">
                  {t('admin:userDetail.noEntities', { defaultValue: 'Niciuna asignată' })}
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {u.assignedEntitatiSilvice.map((e, idx) => (
                    <span
                      key={`${e}-${idx}`}
                      className={
                        idx === 0
                          ? 'inline-flex items-center gap-1 rounded-full bg-forest-100 px-2 py-1 text-xs font-semibold text-forest-800 dark:bg-forest-900/40 dark:text-forest-200'
                          : 'inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                      }
                    >
                      {e}
                      {idx === 0 ? (
                        <span className="text-[9px] uppercase opacity-70">
                          {t('admin:userDetail.default', { defaultValue: 'implicită' })}
                        </span>
                      ) : null}
                    </span>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <MapPin className="h-4 w-4" />
                {t('admin:userDetail.defaults', { defaultValue: 'Metadata implicită' })}
              </h2>
            </CardHeader>
            <CardBody className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <Row label={t('auth:profile.defaultRaion')}>{u.defaultRaion ?? '—'}</Row>
              <Row label={t('auth:profile.defaultPrimarie')}>{u.defaultPrimarie ?? '—'}</Row>
              <Row label={t('auth:profile.defaultUcf')}>
                {u.defaultUcf ? <code className="font-mono">{u.defaultUcf}</code> : '—'}
              </Row>
              <Row label={t('auth:profile.defaultOcolSilvic')}>{u.defaultOcolSilvic ?? '—'}</Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                <Clock className="h-4 w-4" />
                {t('admin:userDetail.activity', { defaultValue: 'Activitate' })}
              </h2>
            </CardHeader>
            <CardBody className="space-y-3 text-sm">
              <Row icon={Calendar} label={t('admin:userDetail.createdAt', { defaultValue: 'Creat' })}>
                {fmt(u.createdAt)}
              </Row>
              <Row icon={Clock} label={t('admin:userDetail.updatedAt', { defaultValue: 'Actualizat' })}>
                {fmt(u.updatedAt)}
              </Row>
              <Row icon={Clock} label={t('admin:userDetail.lastLogin', { defaultValue: 'Ultima conectare' })}>
                {fmt(u.lastLoginAt)}
              </Row>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  children,
}: {
  icon?: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex w-44 flex-shrink-0 items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        {label}
      </div>
      <div className="flex-1 text-slate-800 dark:text-slate-200">{children}</div>
    </div>
  );
}
